/**
 * Alerts Store
 * Manages alerts data with polling
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { servicesContainer } from '@/core/di/services-container';
import { AlertSummary, Alert, AlertSeverity } from '../types/dashboard-types';
import { AlertFilters } from '../types/filter-types';

// Polling interval for alerts (60 seconds)
const ALERTS_POLL_INTERVAL = 60000;

interface AlertsState {
  // Data
  summary: AlertSummary | null;
  alerts: Alert[];
  totalCount: number;

  // Filters
  filters: AlertFilters;
  selectedSeverities: AlertSeverity[];

  // Loading states
  isLoadingSummary: boolean;
  isLoadingAlerts: boolean;

  // Error states
  summaryError: string | null;
  alertsError: string | null;

  // Polling state
  pollIntervalId: ReturnType<typeof setInterval> | null;
  lastUpdate: Date | null;

  // Actions
  loadSummary: () => Promise<void>;
  loadAlerts: () => Promise<void>;
  loadAll: () => Promise<void>;
  setFilters: (filters: Partial<AlertFilters>) => void;
  toggleSeverity: (severity: AlertSeverity) => void;
  clearFilters: () => void;
  startPolling: () => void;
  stopPolling: () => void;
  reset: () => void;
}

const initialState = {
  summary: null,
  alerts: [],
  totalCount: 0,
  filters: {},
  selectedSeverities: [],
  isLoadingSummary: false,
  isLoadingAlerts: false,
  summaryError: null,
  alertsError: null,
  pollIntervalId: null,
  lastUpdate: null,
};

export const useAlertsStore = create<AlertsState>()(
  persist(
    (set, get) => ({
      ...initialState,

      loadSummary: async () => {
        set({ isLoadingSummary: true, summaryError: null });

        try {
          const services = servicesContainer.getServices();
          const result = await services.getAlertsUseCase.executeSummary();

          if (result.hasError) {
            set({
              summaryError: result.errorMessage,
              isLoadingSummary: false,
            });
            return;
          }

          set({
            summary: result.summary,
            isLoadingSummary: false,
            lastUpdate: new Date(),
          });
        } catch (error) {
          set({
            summaryError: error instanceof Error ? error.message : 'Failed to load alerts summary',
            isLoadingSummary: false,
          });
        }
      },

      loadAlerts: async () => {
        const { filters, selectedSeverities } = get();
        set({ isLoadingAlerts: true, alertsError: null });

        try {
          const services = servicesContainer.getServices();

          // Build filters including selected severities
          const apiFilters: AlertFilters = {
            ...filters,
            ...(selectedSeverities.length > 0 ? { severity: selectedSeverities } : {}),
          };

          const result = await services.getAlertsUseCase.executeList(apiFilters);

          if (result.hasError) {
            set({
              alertsError: result.errorMessage,
              isLoadingAlerts: false,
            });
            return;
          }

          set({
            alerts: result.alerts,
            totalCount: result.totalCount,
            isLoadingAlerts: false,
          });
        } catch (error) {
          set({
            alertsError: error instanceof Error ? error.message : 'Failed to load alerts',
            isLoadingAlerts: false,
          });
        }
      },

      loadAll: async () => {
        await Promise.all([get().loadSummary(), get().loadAlerts()]);
      },

      setFilters: (newFilters: Partial<AlertFilters>) => {
        const { filters } = get();
        set({ filters: { ...filters, ...newFilters } });
        get().loadAlerts();
      },

      toggleSeverity: (severity: AlertSeverity) => {
        const { selectedSeverities } = get();
        const newSeverities = selectedSeverities.includes(severity)
          ? selectedSeverities.filter(s => s !== severity)
          : [...selectedSeverities, severity];
        set({ selectedSeverities: newSeverities });
        get().loadAlerts();
      },

      clearFilters: () => {
        set({ filters: {}, selectedSeverities: [] });
        get().loadAlerts();
      },

      startPolling: () => {
        const { pollIntervalId } = get();

        // Clear existing interval
        if (pollIntervalId) clearInterval(pollIntervalId);

        // Start new polling interval
        const newInterval = setInterval(() => {
          get().loadAll();
        }, ALERTS_POLL_INTERVAL);

        set({ pollIntervalId: newInterval });
      },

      stopPolling: () => {
        const { pollIntervalId } = get();
        if (pollIntervalId) clearInterval(pollIntervalId);
        set({ pollIntervalId: null });
      },

      reset: () => {
        get().stopPolling();
        set(initialState);
      },
    }),
    {
      name: 'alerts-storage',
      partialize: (state) => ({
        selectedSeverities: state.selectedSeverities,
      }),
    }
  )
);
